import React, {useEffect, useMemo, useState} from "react";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import {Dropdown} from "primereact/dropdown";
import {Tag} from "primereact/tag";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {Calendar} from "primereact/calendar";
import {Card} from "primereact/card";
import {FloatLabel} from "primereact/floatlabel";
import dayjs from "dayjs";

import 'primeicons/primeicons.css';
import 'primereact/resources/themes/md-light-deeppurple/theme.css';
import 'primereact/resources/primereact.min.css';
import "./RMAgrid.css"
import {ToJsDate} from "../../utilities/ToMySqlDate";
import {generateRandomString} from "../../utilities/randomGenerator";

export default function RMAGrid({
                                   data,
                                   setData,
                                   technician,
                                   setAction,
                                   selectedDate
                               }) {

    const emptyRow = useMemo(() => ({
        id: generateRandomString(4),
        date: dayjs(selectedDate ?? new Date()).format("YYYY-MM-DD"),
        bookId: 0,
        customer: "",
        description: "",
        technician: "",
        warranty: false,
        finished: false,
        diagnostic: "",
        delivered: false,
        deliveryDate: "",              // <- set when Delivered is checked
        quantity: 0,
        repeat: false,
        repeatCount: 0,
        completionDate: "",            // <- set when Repaired is checked
        expenses: 0,
        charges: 0,
        comment: "",
    }), [selectedDate]);

    const [selectedRow, setSelectedRow] = useState(null);
    const [form, setForm] = useState(emptyRow);

    useEffect(() => {
        // If user changes date on parent, keep form's date aligned for new entries (but don’t overwrite when editing).
        if (!selectedRow) {
            setForm(prev => ({...prev, date: dayjs(selectedDate ?? new Date()).format("YYYY-MM-DD")}));
        }
    }, [selectedDate, selectedRow]);

    useEffect(() => {
        // If data becomes empty for any reason, keep a usable form.
        if (!Array.isArray(data) || data.length === 0) {
            setSelectedRow(null);
            setForm(emptyRow);
        }
    }, [data, emptyRow]);

    const setField = (field, value) => {
        setForm(prev => ({...prev, [field]: value}));
    };

    const parseCalendarToMysql = (jsDate) => {
        if (!jsDate) return dayjs().format("YYYY-MM-DD");
        return dayjs(jsDate).format("YYYY-MM-DD");
    };

    const onNew = () => {
        setSelectedRow(null);
        setForm({
            ...emptyRow,
            id: generateRandomString(4),
            date: dayjs(selectedDate ?? new Date()).format("YYYY-MM-DD"),
            deliveryDate: "",          // <- reset; will be set by checkbox
            completionDate: "",        // <- reset; will be set by checkbox
        });
    };

    const validate = () => {
        if (!form.customer?.trim()) return "Customer is required.";
        if (!form.description?.trim()) return "Description is required.";
        return null;
    };

    const onSave = () => {
        const err = validate();
        if (err) return;

        const rowToSave = {
            ...form,
            // auto dates when toggles are on (keeps behavior similar to your table checkbox logic)
            deliveryDate: form.delivered
                ? (form.deliveryDate || dayjs().format("YYYY-MM-DD"))
                : form.deliveryDate,
            completionDate: form.finished
                ? (form.completionDate || dayjs().format("YYYY-MM-DD"))
                : form.completionDate,
        };

        setData(prev => {
            const prevArr = Array.isArray(prev) ? prev : [];
            const exists = prevArr.some(it => it?.id === rowToSave.id);
            const next = exists
                ? prevArr.map(it => (it?.id === rowToSave.id ? rowToSave : it))
                : [rowToSave, ...prevArr];
            return next;
        });

        setAction({mode: "Save", _data: rowToSave});
        setSelectedRow(rowToSave);
    };

    const onDelete = (rowData) => {
        if (!rowData?.id) return;

        setAction({mode: "Delete", _data: rowData});

        setData(prev => {
            const prevArr = Array.isArray(prev) ? prev : [];
            const next = prevArr.filter(it => it?.id !== rowData.id);

            if (selectedRow?.id === rowData.id) {
                setSelectedRow(null);
                setForm(emptyRow);
            }

            return next.length > 0 ? next : [emptyRow];
        });
    };

    const dateBody = (rowData, column) => {
        const dt = dayjs(rowData?.[column.field]);
        return <Tag value={ToJsDate(dt).format("DD-MM-YYYY")} />;
    };

    const boolBody = (rowData, col) => (
        <Checkbox checked={Boolean(rowData?.[col.field])} readOnly />
    );

    const moneyBody = (rowData, col) => {
        const val = Number(rowData?.[col.field] ?? 0);
        return <span>{val.toLocaleString()}</span>;
    };

    const removeBody = (rowData) => (
        <Button
            type="button"
            icon="pi pi-trash"
            className="p-button-sm p-button-text"
            disabled={rowData?.posted}
            onClick={() => onDelete(rowData)}
        />
    );

    return (
        <div className="w-full">
            <div className="grid bg-gray-900 rma-shell ">
                {/* LEFT: Entry panel (CashBook-like) */}
                <div className="col-3 rma-panel h-full p-1">
                    <Card
                        className="rma-card h-full "
                        pt={{
                            body: { className: "p-2" },
                            content: { className: "p-0" },
                        }}
                        style={{
                            borderRadius: "10px",
                            backgroundColor: "#70716E",
                            border: "1px solid rgba(255,255,255,0.10)",
                            boxShadow:
                                "inset 2px 2px 6px rgba(255,255,255,0.22), inset -2px -2px 6px rgba(0,0,0,0.35), 0 10px 24px rgba(0,0,0,0.25)",
                        }}
                    >
                        <div className="rma-form">
                            <FloatLabel>
                                <Calendar
                                    inputId="rma-date"
                                    value={dayjs(form.date).toDate()}
                                    onChange={(e) => setField("date", parseCalendarToMysql(e.value))}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    className="rma-input"
                                    inputClassName="rma-input-inner"
                                    selectionMode="single"
                                />
                                <label htmlFor="rma-date">Date</label>
                            </FloatLabel>

                            <FloatLabel>
                                <InputNumber
                                    inputId="rma-book-ref"
                                    value={form.bookId}
                                    onValueChange={(e) => setField("bookId", e.value ?? 0)}
                                    className="rma-input"
                                    inputClassName="rma-input-inner"
                                />
                                <label htmlFor="rma-book-ref">Book Ref</label>
                            </FloatLabel>

                            <FloatLabel>
                                <InputText
                                    id="rma-customer"
                                    value={form.customer}
                                    onChange={(e) => setField("customer", e.target.value)}
                                    className="rma-input"
                                />
                                <label htmlFor="rma-customer">Customer</label>
                            </FloatLabel>

                            <FloatLabel>
                                <InputText
                                    id="rma-description"
                                    value={form.description}
                                    onChange={(e) => setField("description", e.target.value)}
                                    className="rma-input"
                                />
                                <label htmlFor="rma-description">Description</label>
                            </FloatLabel>

                            <FloatLabel>
                                <Dropdown
                                    inputId="rma-technician"
                                    value={form.technician}
                                    options={technician || []}
                                    onChange={(e) => setField("technician", e.value)}
                                    placeholder="Select"
                                    className="rma-input"
                                />
                                <label htmlFor="rma-technician">Technician</label>
                            </FloatLabel>

                            <FloatLabel>
                                <InputText
                                    id="rma-diagnostics"
                                    value={form.diagnostic}
                                    onChange={(e) => setField("diagnostic", e.target.value)}
                                    className="rma-input"
                                />
                                <label htmlFor="rma-diagnostics">Diagnostics</label>
                            </FloatLabel>

                            <div className="rma-grid-2">
                                <FloatLabel>
                                    <InputNumber
                                        inputId="rma-quantity"
                                        value={form.quantity}
                                        onValueChange={(e) => setField("quantity", e.value ?? 0)}
                                        className="rma-input"
                                        inputClassName="rma-input-inner"
                                    />
                                    <label htmlFor="rma-quantity">Quantity</label>
                                </FloatLabel>

                                <FloatLabel>
                                    <InputNumber
                                        inputId="rma-repeat-count"
                                        value={form.repeatCount}
                                        disabled={!form.repeat}
                                        onValueChange={(e) => setField("repeatCount", e.value ?? 0)}
                                        className="rma-input"
                                        inputClassName="rma-input-inner"
                                    />
                                    <label htmlFor="rma-repeat-count">Repeat Count</label>
                                </FloatLabel>
                            </div>

                            <div className="rma-grid-2">
                                <FloatLabel>
                                    <InputNumber
                                        inputId="rma-expenses"
                                        value={form.expenses}
                                        onValueChange={(e) => setField("expenses", e.value ?? 0)}
                                        className="rma-input"
                                        inputClassName="rma-input-inner"
                                    />
                                    <label htmlFor="rma-expenses">Expenses</label>
                                </FloatLabel>

                                <FloatLabel>
                                    <InputNumber
                                        inputId="rma-charges"
                                        value={form.charges}
                                        onValueChange={(e) => setField("charges", e.value ?? 0)}
                                        className="rma-input"
                                        inputClassName="rma-input-inner"
                                    />
                                    <label htmlFor="rma-charges">Charges</label>
                                </FloatLabel>
                            </div>

                            <FloatLabel>
                                <InputText
                                    id="rma-comment"
                                    value={form.comment}
                                    onChange={(e) => setField("comment", e.target.value)}
                                    className="rma-input"
                                />
                                <label htmlFor="rma-comment">Comment</label>
                            </FloatLabel>

                            <div className="rma-divider" />

                            <div className="rma-grid-2 rma-checks">
                                <label className="rma-check">
                                    <Checkbox
                                        checked={form.warranty}
                                        onChange={(e) => setField("warranty", e.checked)}
                                    />
                                    <span>Warranty</span>
                                </label>

                                <label className="rma-check">
                                    <Checkbox
                                        checked={form.repeat}
                                        onChange={(e) => {
                                            setField("repeat", e.checked);
                                            if (!e.checked) setField("repeatCount", 0);
                                        }}
                                    />
                                    <span>Repeated</span>
                                </label>

                                <label className="rma-check">
                                    <Checkbox
                                        checked={form.finished}
                                        onChange={(e) => {
                                            const checked = Boolean(e.checked);
                                            setField("finished", checked);
                                            setField("completionDate", checked ? dayjs().format("YYYY-MM-DD") : "");
                                        }}
                                    />
                                    <span>Repaired{form.completionDate ? ` (${form.completionDate})` : ""}</span>
                                </label>

                                <label className="rma-check">
                                    <Checkbox
                                        checked={form.delivered}
                                        onChange={(e) => {
                                            const checked = Boolean(e.checked);
                                            setField("delivered", checked);
                                            setField("deliveryDate", checked ? dayjs().format("YYYY-MM-DD") : "");
                                        }}
                                    />
                                    <span>Delivered{form.deliveryDate ? ` (${form.deliveryDate})` : ""}</span>
                                </label>
                            </div>

                            <div className="rma-actions">
                                <Button
                                    label="New"
                                    icon="pi pi-plus"
                                    className="p-button-secondary p-button-sm"
                                    onClick={onNew}
                                />
                                <Button
                                    label="Save"
                                    icon="pi pi-save"
                                    className="p-button-success p-button-sm"
                                    onClick={onSave}
                                />
                                <Button
                                    label="Delete"
                                    icon="pi pi-trash"
                                    className="p-button-danger p-button-sm"
                                    disabled={!selectedRow || Boolean(selectedRow?.posted)}
                                    onClick={() => selectedRow && onDelete(selectedRow)}
                                />
                            </div>
                        </div>
                    </Card>
                </div>

                {/* RIGHT: DataTable */}
                <div className="col-9 rma-table">
                    <Card className="shadow-1 rma-table-card">
                        <DataTable
                            value={Array.isArray(data) ? data : []}
                            className="p-datatable-striped p-datatable-gridlines"
                            size="small"
                            style={{fontSize: "9px"}}
                            selectionMode="single"
                            selection={selectedRow}
                            onSelectionChange={(e) => {
                                setSelectedRow(e.value);
                                if (e.value) setForm({...e.value});
                            }}
                            dataKey="id"
                            scrollable
                            scrollHeight="80vh"
                        >
                            <Column field="warranty" header="Warranty" body={boolBody} headerStyle={{backgroundColor: "#fff0f0"}} />
                            <Column field="repeat" header="Repeated" body={boolBody} headerStyle={{backgroundColor: "#fff0f0"}} />
                            <Column field="finished" header="Repaired" body={boolBody} headerStyle={{backgroundColor: "#fff0f0"}} />
                            <Column field="delivered" header="Delivered" body={boolBody} headerStyle={{backgroundColor: "#fff0f0"}} />

                            <Column field="bookId" header="Book Ref" sortable headerStyle={{backgroundColor: "#fff0f0"}} />
                            <Column field="date" header="Date" body={dateBody} sortable headerStyle={{backgroundColor: "#fff0f0"}} />
                            <Column field="customer" header="Customer" sortable headerStyle={{backgroundColor: "#fff0f0"}} />
                            <Column field="description" header="Description" headerStyle={{backgroundColor: "#fff0f0"}} />
                            <Column field="technician" header="Technician" sortable headerStyle={{backgroundColor: "#fff0f0"}} />
                            <Column field="diagnostic" header="Diagnostics" headerStyle={{backgroundColor: "#fff0f0"}} />
                            <Column field="quantity" header="Qty" sortable headerStyle={{backgroundColor: "#fff0f0"}} />
                                 <Column field="comment" header="Comment" headerStyle={{backgroundColor: "#fff0f0"}} />

                          </DataTable>
                    </Card>
                </div>
            </div>
        </div>
    );
}